import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  MapPin, CheckCircle, AlertCircle, Lock, ArrowRight,
  Shield, AlertTriangle, Flame, TrendingUp, ChevronRight, Zap, Plus, ShoppingCart, Tag, X
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { authHeaders } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { INDUSTRY_TYPES, CLAIMABLE_INDUSTRY_TYPES, getIndustryType } from "@/constants/industries";
import ResendVerificationLink from "@/components/ResendVerificationLink";
import { getErrorMessage, getErrorCode } from "@/lib/apiErrors";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const FALLBACK_PRICING = {1: 150, 2: 280, 3: 375};
const CLAIMABLE_INDUSTRIES = CLAIMABLE_INDUSTRY_TYPES;

function NearbyZipCard({ zip, city, state, available, onClaim }) {
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border ${
        available
          ? "bg-[#F0FDF4] border-[#2ECC71]/30 cursor-pointer hover:border-[#2ECC71] transition-colors"
          : "bg-[#FEF2F2] border-red-200 opacity-70"
      }`}
      onClick={() => available && onClaim(zip)}
      data-testid={`nearby-zip-${zip}`}
    >
      <div>
        <span className="font-bold text-[#0F172A] text-sm">{zip}</span>
        {city && <span className="text-[#64748B] text-xs ml-2">{city}, {state}</span>}
      </div>
      {available ? (
        <span className="text-xs font-bold text-[#27AE60] bg-[#2ECC71]/10 px-2 py-1 rounded-full flex items-center gap-1">
          <CheckCircle size={11} /> AVAILABLE
        </span>
      ) : (
        <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full flex items-center gap-1">
          <Lock size={11} /> CLAIMED
        </span>
      )}
    </div>
  );
}

export default function ClaimZip() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [advertiserType, setAdvertiserType] = useState("tax_pro");
  const [zipInput, setZipInput] = useState(searchParams.get("zip") || "");
  const [checkedZip, setCheckedZip] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [nearbyZips, setNearbyZips] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [cart, setCart] = useState([]); // [{zip, advertiserType, city, state, industryLabel}]
  const [pricing, setPricing] = useState(FALLBACK_PRICING);
  const [referralCredit, setReferralCredit] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponStatus, setCouponStatus] = useState(null); // null | { valid, discount_pct, message } | { error }
  const [couponLoading, setCouponLoading] = useState(false);

  const [form, setForm] = useState({
    business_name: user?.business_name || "",
    contact_name: user?.name || "",
    phone: "",
    city: "",
    state: "",
    address: "",
    website: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitErrorCode, setSubmitErrorCode] = useState(null);
  const claimFormRef = useRef(null);

  // Email capture for non-logged-in visitors
  const [holdEmail, setHoldEmail] = useState("");
  const [holdSent, setHoldSent] = useState(false);
  const [holdSending, setHoldSending] = useState(false);

  useEffect(() => {
    if (searchParams.get("zip")) {
      checkZipAvailability(searchParams.get("zip"));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        contact_name: f.contact_name || user.name || "",
        business_name: f.business_name || user.business_name || "",
      }));
    }
  }, [user]);

  const checkZipAvailability = async (z) => {
    if (!z || z.length !== 5) { setCheckError("Please enter a valid 5-digit ZIP code."); return; }
    setChecking(true);
    setCheckError("");
    setAvailability(null);
    setNearbyZips([]);
    try {
      const res = await axios.get(`${API}/zips/availability?zip=${z}&type=${advertiserType}`);
      setAvailability(res.data);
      setCheckedZip(z);

      // Auto-fill city/state from zip info if available
      if (res.data.zip_info) {
        setForm(f => ({
          ...f,
          city: f.city || res.data.zip_info.city || "",
          state: f.state || res.data.zip_info.state || "",
        }));
      }

      // If taken, load nearby zips for FOMO
      if (!res.data.available) {
        setLoadingNearby(true);
        try {
          const nb = await axios.get(`${API}/zips/nearby?zip=${z}&limit=6`);
          setNearbyZips(nb.data.nearby || []);
        } catch { /* silent */ }
        setLoadingNearby(false);
      }
    } catch {
      setCheckError("Failed to check ZIP code. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleCheckSubmit = (e) => {
    e.preventDefault();
    checkZipAvailability(zipInput);
  };

  // Auto-detect city/state when user finishes typing zip
  const handleZipBlur = async () => {
    if (zipInput.length === 5 && availability?.available) {
      try {
        const res = await axios.get(`${API}/zips/lookup?zip=${zipInput}`);
        setForm(f => ({
          ...f,
          city: f.city || res.data.city || "",
          state: f.state || res.data.state || "",
        }));
      } catch { /* silent */ }
    }
  };

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleClaim = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    setSubmitError("");
    setSubmitting(true);
    try {
      const zipInfo = availability?.zip_info || {};
      const city = zipInfo.city || form.city || "";
      const state = zipInfo.state || form.state || "";
      // Abandoned checkout recovery
      axios.post(`${API}/leads/reserve-zip`, {
        email: user.email,
        zip_code: checkedZip,
        advertiser_type: advertiserType,
        city,
        state,
      }).catch(() => {});

      const res = await axios.post(`${API}/payments/bulk-checkout`, {
        zips: [{ zip_code: checkedZip, advertiser_type: advertiserType, city, state }],
        business_name: form.business_name,
        contact_name: form.contact_name,
        phone: form.phone,
        address: form.address || "",
        website: form.website || "",
        description: form.description || "",
        origin_url: window.location.origin,
        coupon_code: couponCode || undefined,
      }, { headers: authHeaders() });
      window.location.href = res.data.url;
    } catch (err) {
      setSubmitError(getErrorMessage(err, "Failed to start checkout. Please try again."));
      setSubmitErrorCode(getErrorCode(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch dynamic pricing + referral credit; save ?ref= param for referral tracking
  useEffect(() => {
    axios.get(`${API}/pricing`).then(res => {
      setPricing({1: res.data.single, 2: res.data.two, 3: res.data.three});
    }).catch(() => {});
    if (user) {
      axios.get(`${API}/referral/stats`, { headers: authHeaders() }).then(res => {
        setReferralCredit(res.data.credit_balance || 0);
      }).catch(() => {});
    }
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("ndr_ref", ref.toUpperCase());
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCartSavings = (len) => {
    const perZipRate = pricing[3] ? pricing[3] / 3 : 125;
    if (len <= 3) return Math.max(0, Math.round(len * (pricing[1] || 150) - (pricing[len] || len * (pricing[1] || 150))));
    return Math.max(0, Math.round(len * (pricing[1] || 150) - Math.round(perZipRate * len)));
  };
  const getCartTotal = (len) => {
    if (len <= 3 && pricing[len]) return pricing[len];
    const perZipRate = pricing[3] ? pricing[3] / 3 : 125;
    return Math.round(perZipRate * len);
  };

  const handleValidateCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponStatus(null);
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: couponInput.trim() });
      setCouponStatus({ valid: true, ...res.data });
      setCouponCode(res.data.code);
    } catch (err) {
      setCouponStatus({ valid: false, error: err?.response?.data?.detail || "Invalid promo code" });
      setCouponCode("");
    }
    setCouponLoading(false);
  };

  const removeCoupon = () => {
    setCouponCode("");
    setCouponInput("");
    setCouponStatus(null);
  };

  const handleAddToCart = () => {    if (!checkedZip || !availability?.available || cart.length >= 50) return;
    if (cart.some(item => item.zip === checkedZip && item.advertiserType === advertiserType)) {
      setSubmitError("This ZIP + industry combination is already in your cart.");
      return;
    }
    const zipInfo = availability?.zip_info || {};
    setCart(prev => [...prev, {
      zip: checkedZip,
      advertiserType,
      city: zipInfo.city || form.city || "",
      state: zipInfo.state || form.state || "",
      industryLabel: getIndustryType(advertiserType)?.label || advertiserType,
    }]);
    setZipInput("");
    setAvailability(null);
    setCheckedZip(null);
    setNearbyZips([]);
    setSubmitError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBulkCheckout = async () => {
    if (!user) { navigate("/login"); return; }
    if (cart.length === 0) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      // Abandoned checkout recovery for all cart items
      cart.forEach(item => {
        axios.post(`${API}/leads/reserve-zip`, {
          email: user.email,
          zip_code: item.zip,
          advertiser_type: item.advertiserType,
          city: item.city,
          state: item.state,
        }).catch(() => {});
      });

      const res = await axios.post(`${API}/payments/bulk-checkout`, {
        zips: cart.map(item => ({
          zip_code: item.zip,
          advertiser_type: item.advertiserType,
          city: item.city,
          state: item.state,
        })),
        business_name: form.business_name,
        contact_name: form.contact_name,
        phone: form.phone,
        address: form.address || "",
        website: form.website || "",
        description: form.description || "",
        origin_url: window.location.origin,
        coupon_code: couponCode || undefined,
      }, { headers: authHeaders() });
      window.location.href = res.data.url;
    } catch (err) {
      setSubmitError(getErrorMessage(err, "Failed to start checkout. Please try again."));
      setSubmitErrorCode(getErrorCode(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleHoldSpot = async (e) => {
    e.preventDefault();
    if (!holdEmail) return;
    setHoldSending(true);
    try {
      const zipInfo = availability?.zip_info || {};
      await axios.post(`${API}/leads/reserve-zip`, {
        email: holdEmail,
        zip_code: checkedZip,
        advertiser_type: advertiserType,
        city: zipInfo.city || "",
        state: zipInfo.state || "",
      });
      setHoldSent(true);
    } catch { /* silent */ }
    setHoldSending(false);
  };

  const handleClaimNearby = (zip) => {
    setZipInput(zip);
    setAvailability(null);
    setCheckedZip(null);
    setNearbyZips([]);
    checkZipAvailability(zip);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const zipCity = availability?.zip_info?.city || availability?.provider?.city || "";
  const zipState = availability?.zip_info?.state || availability?.provider?.state || "";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SEOHead
        title="Claim Your ZIP Code Territory — $150/Year"
        description="Secure exclusive local advertising rights for your ZIP code. One advertiser per ZIP per industry. $150 single ZIP, $280 for 2, $375 for 3. 12-month listing."
        canonical="/claim"
        jsonLd={{
          "@type": "Service",
          "name": "Claim ZIP Code Territory",
          "description": "Exclusive local advertising by ZIP code for 12 months. $150/year.",
          "offers": { "@type": "Offer", "price": "150.00", "priceCurrency": "USD" }
        }}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Claim ZIP Code", url: "/claim" },
        ]}
      />
      <Navbar />

      <section className="bg-[#003366] py-10">
        <div className="section-container">
          <h1 className="text-3xl font-black text-white mb-2">Claim Your ZIP Code</h1>
          <p className="text-slate-300">Secure exclusive access to your territory for 12 months — $150/year</p>
        </div>
      </section>

      <div className="section-container py-10 max-w-2xl">

        {/* Industry Type Selector */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="industry-selector">
          <h2 className="text-base font-bold text-[#0F172A] mb-1">Select Your Industry</h2>
          <p className="text-[#64748B] text-sm mb-4">Each industry gets exclusive rights to a ZIP code. One advertiser per ZIP per industry — $150/year.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CLAIMABLE_INDUSTRIES.map(ind => {
              const IconComp = ind.icon;
              const isSelected = advertiserType === ind.value;
              return (
                <button
                  key={ind.value}
                  type="button"
                  onClick={() => {
                    setAdvertiserType(ind.value);
                    if (checkedZip) { setAvailability(null); checkZipAvailability(checkedZip); }
                  }}
                  data-testid={`industry-${ind.value}`}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${isSelected ? `${ind.border} ${ind.bg}` : "border-slate-200 hover:border-slate-300"}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    <IconComp size={16} style={{ color: isSelected ? ind.color : "#64748B" }} />
                  </div>
                  <div>
                    <div className="font-bold text-xs text-[#0F172A] leading-tight">{ind.label}</div>
                    <div className="text-xs text-[#64748B] leading-tight mt-0.5 hidden sm:block">{ind.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        {/* Bulk ZIP Cart — shown when cart has items */}
        {cart.length > 0 && (
          <div className="bg-[#003366] rounded-xl p-5 mb-6 border border-[#004d99]/50" data-testid="bulk-cart-section">
            <div className="flex items-center gap-3 mb-4">
              <ShoppingCart size={20} className="text-[#2ECC71]" />
              <h2 className="text-white font-bold text-lg">Your ZIP Order ({cart.length} ZIP{cart.length !== 1 ? "s" : ""})</h2>
              <div className="ml-auto">
                <span className="text-[#2ECC71] font-black text-2xl">${getCartTotal(cart.length)}</span>
                {referralCredit > 0 && (
                  <span className="ml-2 text-xs font-bold text-white bg-[#2ECC71] px-2 py-1 rounded-full">
                    -${Math.min(referralCredit, getCartTotal(cart.length) - 1).toFixed(0)} credit!
                  </span>
                )}
                {getCartSavings(cart.length) > 0 && !referralCredit && (
                  <span className="ml-2 text-xs font-bold text-[#2ECC71] bg-[#2ECC71]/20 px-2 py-1 rounded-full">
                    SAVE ${getCartSavings(cart.length)}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2 mb-4">
              {cart.map((item, i) => (
                <div key={i} className="flex items-center justify-between bg-white/10 rounded-lg px-4 py-2.5" data-testid={`cart-item-${i}`}>
                  <div>
                    <span className="text-white font-bold">ZIP {item.zip}</span>
                    {item.city && <span className="text-slate-300 text-xs ml-2">{item.city}, {item.state}</span>}
                    <span className="text-slate-400 text-xs ml-2">— {item.industryLabel}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCart(cart.filter((_, j) => j !== i))}
                    className="text-slate-400 hover:text-red-400 transition-colors text-xs font-semibold ml-4"
                    data-testid={`remove-cart-item-${i}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {cart.length < 3 ? (
              <p className="text-slate-400 text-xs mb-3 text-center">
                Add {3 - cart.length} more ZIP{3 - cart.length > 1 ? "s" : ""} below to{" "}
                {cart.length === 1
                  ? <strong className="text-[#2ECC71]">save ${getCartSavings(2)}</strong>
                  : <strong className="text-[#2ECC71]">save ${getCartSavings(3)} total</strong>
                }
              </p>
            ) : (
              <p className="text-slate-400 text-xs mb-3 text-center">
                <strong className="text-[#2ECC71]">${getCartTotal(cart.length)}</strong> total · ${Math.round((pricing[3] || 375) / 3)}/ZIP rate · Keep adding ZIPs to the cart below
              </p>
            )}

            {/* ── Promo Code Input ── */}
            <div className="mb-3">
              {couponStatus?.valid ? (
                <div className="flex items-center justify-between bg-[#F0FDF4] border border-[#2ECC71]/40 rounded-xl px-4 py-2.5">
                  <div className="flex items-center gap-2 text-[#15803d]">
                    <CheckCircle size={15} />
                    <span className="text-sm font-bold">{couponStatus.message}</span>
                    <span className="text-xs text-slate-500 font-mono">({couponCode})</span>
                  </div>
                  <button onClick={removeCoupon} className="text-slate-400 hover:text-red-500 transition-colors" data-testid="remove-coupon">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={couponInput}
                      onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponStatus(null); }}
                      onKeyDown={e => e.key === "Enter" && handleValidateCoupon()}
                      placeholder="Promo code"
                      className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-600 bg-slate-700 text-white placeholder-slate-400 focus:outline-none focus:border-[#2ECC71]"
                      data-testid="coupon-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleValidateCoupon}
                    disabled={couponLoading || !couponInput.trim()}
                    className="px-4 py-2 text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
                    style={{ background: "#1a3a6c", color: "#7dd3fc" }}
                    data-testid="coupon-apply-btn"
                  >
                    {couponLoading ? "..." : "Apply"}
                  </button>
                </div>
              )}
              {couponStatus?.error && (
                <p className="text-red-400 text-xs mt-1 ml-1" data-testid="coupon-error">{couponStatus.error}</p>
              )}
            </div>
            {/* ────────────────────── */}

            {submitError && <p className="text-red-400 text-sm mb-3" data-testid="bulk-checkout-error">{submitError}</p>}
            {submitErrorCode === "email_unverified" && (
              <ResendVerificationLink email={user?.email || holdEmail} className="text-sm text-[#2ECC71] font-semibold underline mb-3 inline-block" />
            )}

            <button
              type="button"
              onClick={handleBulkCheckout}
              disabled={submitting || !user}
              className="w-full bg-[#2ECC71] hover:bg-[#27AE60] text-white font-bold py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              data-testid="bulk-checkout-btn"
            >
              {submitting
                ? "Processing..."
                : couponStatus?.valid && couponStatus?.discount_pct === 100
                  ? `Claim ${cart.length} ZIP${cart.length > 1 ? "s" : ""} — FREE`
                  : `Checkout ${cart.length} ZIP${cart.length > 1 ? "s" : ""} — $${getCartTotal(cart.length)}`
              }
              {!submitting && <ArrowRight size={18} />}
            </button>
            {!user && (
              <p className="text-slate-400 text-xs text-center mt-2">
                <Link to="/login" className="text-[#2ECC71] hover:underline">Sign in</Link> to complete checkout
              </p>
            )}
          </div>
        )}

        {/* Step 1: Check ZIP */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="zip-check-card">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 bg-[#003366] text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
            <h2 className="text-lg font-bold text-[#0F172A]">Check ZIP Code Availability</h2>
          </div>

          <form onSubmit={handleCheckSubmit} className="flex gap-3 flex-col sm:flex-row" data-testid="zip-availability-form">
            <div className="relative flex-1">
              <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                type="text"
                placeholder="5-digit ZIP code"
                value={zipInput}
                onChange={e => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
                className="input-field pl-11"
                maxLength={5}
                data-testid="claim-zip-input"
              />
            </div>
            <button type="submit" disabled={checking} className="btn-primary whitespace-nowrap" data-testid="check-availability-btn">
              {checking ? "Checking..." : "Check Availability"}
            </button>
          </form>

          {checkError && <p className="text-red-500 text-sm mt-3" data-testid="zip-check-error">{checkError}</p>}

          {availability?.available && (
            <div className="mt-4 flex items-center gap-2 text-[#27AE60] font-semibold" data-testid="zip-available-msg">
              <CheckCircle size={18} />
              <span>
                ZIP {checkedZip}{zipCity ? ` — ${zipCity}, ${zipState}` : ""} is available! Claim it below.
              </span>
            </div>
          )}
        </div>

        {/* FOMO BLOCK: ZIP is taken */}
        {availability && !availability.available && (
          <div className="mb-6" data-testid="zip-taken-block">
            {/* Header Banner */}
            <div className="bg-[#1A0A0A] rounded-t-xl px-6 py-5 border-b-2 border-red-600">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lock size={20} className="text-white" />
                </div>
                <div>
                  <div className="text-xs font-black uppercase tracking-widest text-red-400 mb-0.5">Territory Locked</div>
                  <h2 className="text-xl font-black text-white">
                    ZIP Code {checkedZip} Is CLAIMED
                    {zipCity ? <span className="text-red-400 font-bold text-base ml-2">— {zipCity}, {zipState}</span> : ""}
                  </h2>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mt-1">
                A competitor has locked in <strong className="text-white">exclusive rights</strong> to this ZIP code. Every taxpayer who searches for a next-day refund provider in this area is sent <strong className="text-white">directly to them</strong> — not to you.
              </p>
            </div>

            {/* Competitor Box */}
            <div className="bg-[#1E0000] border border-red-900/40 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-900/40 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield size={16} className="text-red-400" />
                </div>
                <div>
                  <div className="text-xs text-red-500 font-semibold uppercase tracking-wide mb-0.5">Exclusive Provider in this ZIP</div>
                  <div className="text-white font-bold">{availability.provider?.business_name || "A Local Tax Office"}</div>
                  {(availability.provider?.city || availability.provider?.state) && (
                    <div className="text-slate-400 text-xs">{availability.provider?.city}, {availability.provider?.state}</div>
                  )}
                </div>
                <div className="ml-auto">
                  <span className="text-xs font-black text-red-400 bg-red-900/30 border border-red-800 px-3 py-1.5 rounded-full uppercase tracking-wide">
                    Competitor
                  </span>
                </div>
              </div>
            </div>

            {/* Urgency Message */}
            <div className="bg-[#1A0D00] border-x border-b border-amber-900/40 px-6 py-4">
              <div className="flex items-start gap-3">
                <Flame size={20} className="text-[#FFD700] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-[#FFD700] font-bold text-sm mb-1">Don't Let Them Dominate Your Entire Market</div>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    Adjacent ZIP codes are still open. Claim your neighboring territory now before more competitors move in. Every day you wait is another day of leads going to someone else.
                  </p>
                </div>
              </div>
            </div>

            {/* Nearby Available ZIPs */}
            <div className="bg-white border-x border-b border-slate-200 rounded-b-xl px-6 py-5" data-testid="nearby-zips-section">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-[#003366]" />
                <h3 className="font-bold text-[#0F172A] text-sm">Adjacent ZIP Codes — Claim Before They're Gone</h3>
              </div>

              {loadingNearby && (
                <div className="text-sm text-[#64748B] flex items-center gap-2 py-2">
                  <div className="w-4 h-4 border-2 border-[#003366] border-t-transparent rounded-full animate-spin" />
                  Scanning nearby territories...
                </div>
              )}

              {!loadingNearby && nearbyZips.length > 0 && (
                <div className="space-y-2">
                  {nearbyZips.map(z => (
                    <NearbyZipCard
                      key={z.zip}
                      {...z}
                      onClaim={handleClaimNearby}
                    />
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-[#64748B] mb-3">Click any available ZIP above to claim it, or enter a different ZIP to check manually.</p>
                <button
                  onClick={() => { setZipInput(""); setAvailability(null); setCheckedZip(null); setNearbyZips([]); document.querySelector('[data-testid="claim-zip-input"]')?.focus(); }}
                  className="btn-action w-full flex items-center justify-center gap-2"
                  data-testid="try-different-zip-btn"
                >
                  <MapPin size={16} /> Check a Different ZIP Code
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Fill Details (only if available) */}
        {availability?.available && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" ref={claimFormRef} data-testid="claim-form-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 bg-[#003366] text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
              <h2 className="text-lg font-bold text-[#0F172A]">Your Business Information</h2>
            </div>

            {/* Available ZIP highlight */}
            <div className="bg-[#F0FDF4] border border-[#2ECC71]/30 rounded-lg p-3 mb-5 flex items-center gap-3">
              <CheckCircle size={18} className="text-[#27AE60] flex-shrink-0" />
              <div>
                <div className="font-bold text-[#15803D] text-sm">
                  ZIP {checkedZip}{zipCity ? ` — ${zipCity}, ${zipState}` : ""} is Available!
                </div>
                <div className="text-xs text-[#64748B]">Complete the form below to secure your exclusive territory.</div>
              </div>
            </div>

            {!user && (
              <div className="bg-[#FFD700]/20 border border-[#FFD700]/40 rounded-lg p-4 mb-5" data-testid="login-prompt">
                <div className="flex items-center gap-2 font-semibold text-[#856404] mb-1">
                  <Lock size={16} /> Sign in to complete your claim
                </div>
                <p className="text-sm text-[#64748B] mb-3">You need an account to claim a ZIP code and process payment.</p>
                <div className="flex gap-3 mb-4">
                  <Link to={`/login?redirect=/claim?zip=${checkedZip}`} className="btn-primary !py-2 !px-4 !text-sm" data-testid="claim-login-btn">Sign In</Link>
                  <Link to="/register" className="btn-outline !py-2 !px-4 !text-sm" data-testid="claim-register-btn">Create Account</Link>
                </div>
                {/* Email capture for visitors not ready to sign up */}
                {!holdSent ? (
                  <div className="border-t border-[#FFD700]/30 pt-3">
                    <p className="text-xs text-[#64748B] mb-2 font-medium">Not ready yet? Enter your email and we'll hold this ZIP for you:</p>
                    <form onSubmit={handleHoldSpot} className="flex gap-2" data-testid="hold-spot-form">
                      <input
                        type="email"
                        required
                        placeholder="your@email.com"
                        value={holdEmail}
                        onChange={e => setHoldEmail(e.target.value)}
                        className="input-field !py-2 !text-sm flex-1"
                        data-testid="hold-email-input"
                      />
                      <button type="submit" disabled={holdSending} className="btn-action !py-2 !px-4 !text-sm whitespace-nowrap" data-testid="hold-spot-btn">
                        {holdSending ? "..." : "Hold My Spot"}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="border-t border-[#FFD700]/30 pt-3 flex items-center gap-2 text-[#27AE60] text-sm font-semibold" data-testid="hold-spot-confirmation">
                    <CheckCircle size={16} /> Got it! Check your email — we'll remind you before this ZIP is taken.
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleClaim} className="space-y-4" data-testid="claim-business-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Business Name *</label>
                  <input name="business_name" type="text" required value={form.business_name} onChange={handleFormChange} className="input-field" placeholder="ABC Tax Services" data-testid="claim-business-name" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Contact Name *</label>
                  <input name="contact_name" type="text" required value={form.contact_name} onChange={handleFormChange} className="input-field" placeholder="Your Name" data-testid="claim-contact-name" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Phone Number *</label>
                  <input name="phone" type="tel" required value={form.phone} onChange={handleFormChange} className="input-field" placeholder="(555) 000-0000" data-testid="claim-phone" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Website</label>
                  <input name="website" type="url" value={form.website} onChange={handleFormChange} className="input-field" placeholder="https://yourbusiness.com" data-testid="claim-website" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                    City * <span className="font-normal text-[#64748B] text-xs">(auto-detected from ZIP)</span>
                  </label>
                  <input name="city" type="text" required value={form.city} onChange={handleFormChange} className="input-field" placeholder="City" data-testid="claim-city" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">
                    State * <span className="font-normal text-[#64748B] text-xs">(auto-detected)</span>
                  </label>
                  <input name="state" type="text" required value={form.state} onChange={handleFormChange} className="input-field" placeholder="CA" maxLength={2} data-testid="claim-state" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Street Address</label>
                <input name="address" type="text" value={form.address} onChange={handleFormChange} className="input-field" placeholder="123 Main St" data-testid="claim-address" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-1.5">Business Description</label>
                <textarea name="description" value={form.description} onChange={handleFormChange} rows={3} className="input-field !h-24 py-3 resize-none" placeholder="Brief description of your tax services..." data-testid="claim-description" />
              </div>

              {submitError && <p className="text-red-500 text-sm" data-testid="claim-submit-error">{submitError}</p>}
              {submitErrorCode === "email_unverified" && (
                <ResendVerificationLink email={user?.email || holdEmail} className="text-sm text-[#003366] font-semibold underline" />
              )}

              <div className="bg-[#F8FAFC] border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-[#0F172A]">
                      ZIP Code {checkedZip} — {getIndustryType(advertiserType)?.label} Exclusive Rights
                    </div>
                    <div className="text-sm text-[#64748B]">Valid for 12 months from purchase</div>
                  </div>
                  <div className="text-2xl font-black text-[#003366]">$150</div>
                </div>
              </div>

              {/* Add to Cart upsell — open ended, up to 50 ZIPs */}
              {cart.length < 50 && user && (
                <div className="bg-[#F0FDF4] border border-[#2ECC71]/30 rounded-lg p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-bold text-[#0F172A]">
                        {cart.length === 0
                          ? `Claim multiple ZIPs and save — best rate $${Math.round((pricing[3] || 375) / 3)}/ZIP`
                          : cart.length === 1
                            ? `Add ${checkedZip} to cart → checkout 2 ZIPs for $${getCartTotal(2)} (save $${getCartSavings(2)}!)`
                            : cart.length === 2
                              ? `Add ${checkedZip} → checkout 3 ZIPs for $${getCartTotal(3)} (save $${getCartSavings(3)} total!)`
                              : `Add ${checkedZip} → ${cart.length + 1} ZIPs for $${getCartTotal(cart.length + 1)} ($${Math.round((pricing[3] || 375) / 3)}/ZIP)`
                        }
                      </div>
                      {cart.length === 0 && (
                        <div className="text-xs text-[#64748B] mt-0.5">2 ZIPs = ${getCartTotal(2)} · 3 ZIPs = ${getCartTotal(3)} · 4+ ZIPs = ${Math.round((pricing[3] || 375) / 3)}/ZIP</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      className="flex-shrink-0 flex items-center gap-1.5 bg-[#003366] text-white text-xs font-bold px-4 py-2.5 rounded-lg hover:bg-[#004080] transition-colors"
                      data-testid="add-to-cart-btn"
                    >
                      <Plus size={14} /> Add to Cart
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !user}
                className="btn-action w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="claim-pay-btn"
              >
                {submitting ? "Redirecting to Checkout..." : `Claim ZIP & Pay $${referralCredit > 0 ? Math.max(1, pricing[1] - Math.min(referralCredit, pricing[1] - 1)) : pricing[1]}`}
                {!submitting && <ArrowRight size={20} />}
              </button>
              {referralCredit > 0 && (
                <p className="text-xs text-center text-[#2ECC71] font-semibold" data-testid="referral-credit-notice">
                  ${Math.min(referralCredit, pricing[1] - 1).toFixed(0)} referral credit will be applied at checkout!
                </p>
              )}
              <p className="text-xs text-center text-[#64748B]">Secure payment powered by Stripe</p>
            </form>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
